import { getEnvironment } from "configuration/endpoints/environments";
import { toTitleCase, hashString } from "helpers/string";
import { ROOT_ROUTE_FOR_ROLE } from "model/routing";
import { useEffect } from "react";
import { useLocation } from "react-router";

export enum GAEventCategories {
  campaignAction = "campaign_action",
  lineItemAction = "line_item_action",
  analyticsAction = "analytics_action",
  trafficAction = "traffic_action",
  inventoryAction = "inventory_action",
  creativesAction = "creatives_action",
  suggestOptsAction = "suggest_opts_action",
  modelAction = "model_action",
  appIntelligenceAction = "app_intelligence_action",
  analyticsAiAgentAction = "analytics_ai_agent_action",
}

const MEASUREMENT_ID = "G-VC54J0XMB3";
const gtag = (window as any).gtag;

export const gtagConfig = (
  location: string,
  pageTitle: string,
  userRole: string,
  organisation: string
) => {
  const isAlpha = window.location.href.includes("alpha");
  // NOTE: set below to true for debugging
  // (to exclude that traffic from GA and enabled debug view)
  const isDebug = false;

  // Get username from localStorage and hash it for user_id
  const username = localStorage.getItem("username");
  const userId = username ? hashString(username) : undefined;

  gtag("config", MEASUREMENT_ID, {
    transport_type: "beacon",
    alwaysSendReferrer: true,
    page_title: `Initial Load: ${toTitleCase(pageTitle) || "Home"}`,
    page_location: location,
    page_path: window.location.href,
    send_page_view: true,
    ...(userId ? { user_id: userId } : {}),
    ...(isDebug ? { debug_mode: true } : {}),
  });

  gtag("set", "user_properties", {
    user_role: userRole,
    environment: isAlpha ? "alpha" : isDebug ? "debug" : "prod",
    organisation,
  });
};

export const trackPageView = (
  location: string,
  pageTitle: string,
  role?: string
) => {
  if (!pageTitle && location === "/")
    pageTitle = role
      ? (ROOT_ROUTE_FOR_ROLE[role] ?? ROOT_ROUTE_FOR_ROLE.DEFAULT)
      : ROOT_ROUTE_FOR_ROLE.DEFAULT;
  if (location.includes("lineItem")) pageTitle = "Line Item";

  gtag("event", "page_view", {
    page_title: `${toTitleCase(pageTitle)}`,
    page_location: location,
    page_path: window.location.href,
  });
};

export const trackEvent = (
  eventName: string,
  eventCategory: GAEventCategories,
  eventLabel?: string
) => {
  gtag("event", eventName, {
    event_category: eventCategory,
    event_label: eventLabel,
    non_interaction: true,
  });
};

let isInitialLoad = true;
let currentLocation = "";

const useGATracker = (role = "", organisation?: string) => {
  const { pathname } = useLocation();
  const isTracking = !!(getEnvironment().useGa && organisation);

  if (isTracking && isInitialLoad) {
    gtagConfig(pathname, pathname.split("/")[1], role, organisation);
    currentLocation = pathname;
    isInitialLoad = false;
  }

  useEffect(() => {
    if (!isTracking) return;

    gtag("set", { send_page_view: false });

    if (pathname !== currentLocation) {
      currentLocation = pathname;
      trackPageView(pathname, pathname.split("/")[1], role);
    }
  }, [isTracking, pathname, role]);
};

export default useGATracker;
